import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Briefcase, FilePdf, Barbell, Camera, ChartLine, Users, ClipboardText,
  Trophy, ArrowRight, Sparkle,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import SportsPrograms from "./SportsPrograms";
import SportsDrills from "./SportsDrills";
import SportsAnalyzer from "./SportsAnalyzer";
import SportsOpponents from "./SportsOpponents";
import SportsMatchDay from "./SportsMatchDay";
import SportsSessions from "./SportsSessions";

const TABS = [
  { key: "overview",  label: "Overview",           icon: Briefcase },
  { key: "programs",  label: "Programs",           icon: FilePdf },
  { key: "drills",    label: "Drills Library",     icon: Barbell },
  { key: "sessions",  label: "Session Plans",      icon: Sparkle },
  { key: "analyzer",  label: "Form Analyzer",      icon: Camera },
  { key: "opponents", label: "Opposition Scouting",icon: ClipboardText },
  { key: "matchday",  label: "Match-Day Playbook", icon: Trophy },
];

function KpiTile({ label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="label-tiny text-[9px] text-slate-500 mb-2">{label}</div>
      <div className="font-heading text-3xl text-cyan-600 tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sports-coach/hub/overview")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-sm text-slate-500" data-testid="sc-overview-loading">Loading…</div>;
  }
  if (!data) {
    return <div className="text-sm text-red-500">Could not load overview.</div>;
  }
  const c = data.counts || {};

  return (
    <div className="space-y-6" data-testid="sc-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiTile label="PROGRAMS"       value={c.programs}         testid="sc-kpi-programs" />
        <KpiTile label="MY DRILLS"      value={c.custom_drills}    testid="sc-kpi-custom-drills" />
        <KpiTile label="SEED DRILLS"    value={c.seed_drills}      testid="sc-kpi-seed-drills" />
        <KpiTile label="OPPONENTS"      value={c.opponents}        testid="sc-kpi-opponents" />
        <KpiTile label="AI ANALYSES"    value={c.analyses}         testid="sc-kpi-analyses" />
        <KpiTile label="UPCOMING MATCH" value={c.upcoming_matches} testid="sc-kpi-upcoming" />
      </div>

      {data.next_match && (
        <div className="border border-cyan-300 bg-cyan-50 p-5" data-testid="sc-next-match">
          <div className="label-tiny text-cyan-700 mb-2">// NEXT MATCH</div>
          <div className="font-heading text-2xl text-slate-900">{data.next_match.title}</div>
          <div className="text-sm text-slate-600 mt-1">
            {new Date(data.next_match.match_date).toDateString()}
            {data.next_match.formation && <> · <span className="text-slate-800">{data.next_match.formation}</span></>}
            {data.next_match.sport && <> · <span className="uppercase tracking-widest text-[10px]">{data.next_match.sport}</span></>}
          </div>
          {data.next_match.tactical_plan && (
            <div className="text-sm text-slate-700 mt-3 whitespace-pre-wrap">{data.next_match.tactical_plan}</div>
          )}
        </div>
      )}

      {data.recent_analyses?.length > 0 && (
        <div className="border border-slate-200 p-4" data-testid="sc-recent-analyses">
          <div className="label-tiny mb-3 text-slate-700">// RECENT AI ANALYSES</div>
          <div className="space-y-2">
            {data.recent_analyses.map((a) => {
              const score = a.result?.score ?? 0;
              const verdict = a.result?.verdict || "—";
              return (
                <div key={a.analysis_id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`sc-recent-analysis-${a.analysis_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-heading truncate">{a.movement || "General technique"}</div>
                    <div className="text-xs text-slate-500 truncate">{a.athlete_name || "—"} · {a.sport}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-heading tabular-nums ${score >= 80 ? "text-emerald-600" : score >= 60 ? "text-amber-600" : "text-red-500"}`}>{score}</div>
                    <div className="text-[10px] tracking-widest text-slate-500">{verdict}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TabQuickLink icon={FilePdf}       to="?tab=programs"  label="UPLOAD PROGRAM"       desc="PDF / scanned image → assigned athletes can view" testid="sc-quick-programs" />
        <TabQuickLink icon={Barbell}       to="?tab=drills"    label="SPORT DRILLS"         desc="Filter by sport · add custom · attach video" testid="sc-quick-drills" />
        <TabQuickLink icon={Camera}        to="?tab=analyzer"  label="AI TECHNIQUE ANALYZER" desc="Upload a photo · Gemini 3.1 Pro gives corrections" testid="sc-quick-analyzer" />
        <TabQuickLink icon={ClipboardText} to="?tab=opponents" label="SCOUT OPPONENTS"      desc="Strengths, weaknesses, key players, notes" testid="sc-quick-opponents" />
        <TabQuickLink icon={Trophy}        to="?tab=matchday"  label="MATCH-DAY PLAYBOOK"    desc="Lineup + formation + tactical plan" testid="sc-quick-matchday" />
      </div>
    </div>
  );
}

function TabQuickLink({ icon: Icon, to, label, desc, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group flex items-center gap-4 border border-slate-200 hover:border-cyan-400 bg-white hover:bg-cyan-50 p-4 transition-colors">
      <Icon size={22} weight="duotone" className="text-cyan-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-heading text-sm text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">{desc}</div>
      </div>
      <ArrowRight size={16} className="text-slate-400 group-hover:text-cyan-600 shrink-0" />
    </Link>
  );
}

export default function SportsCommandDeck() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tab = useMemo(() => {
    const t = new URLSearchParams(location.search).get("tab");
    return TABS.find((x) => x.key === t)?.key || "overview";
  }, [location.search]);

  const setTab = (key) => navigate(`/sports-coach${key === "overview" ? "" : `?tab=${key}`}`);

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="sports-command-deck">
      <div className="mb-6">
        <div className="label-tiny text-cyan-600 mb-2">// SPORTS COACH · COMMAND DECK</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <h1 className="font-heading text-4xl sm:text-5xl">TECHNICAL HUB</h1>
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <Sparkle size={12} className="text-cyan-500" />
            AI powered by Gemini 3.1 Pro
          </div>
        </div>
        <p className="text-slate-600 text-sm mt-2">
          {user?.name?.split(" ")[0] || "Coach"} · Programs, sport-specific drills, technique analysis, opposition scouting & match-day plans.
        </p>
      </div>

      {/* Tab strip */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto no-scrollbar" data-testid="sc-tabs">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`sc-tab-${key}`}
              className={`flex items-center gap-2 px-3 py-2.5 text-xs label-tiny tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                active
                  ? "border-cyan-500 text-cyan-700"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon size={14} weight={active ? "fill" : "duotone"} />
              {label.toUpperCase()}
            </button>
          );
        })}
      </div>

      {tab === "overview"  && <OverviewTab />}
      {tab === "programs"  && <SportsPrograms />}
      {tab === "drills"    && <SportsDrills />}
      {tab === "sessions"  && <SportsSessions />}
      {tab === "analyzer"  && <SportsAnalyzer />}
      {tab === "opponents" && <SportsOpponents />}
      {tab === "matchday"  && <SportsMatchDay />}
    </div>
  );
}
